import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { CSSProperties } from 'react';
import { brandFromHost, isValidEmail } from '@brands/shared';
import { useBrandTheme } from '../data/brands';
import { brandColors } from '../lib/accent';
import { useAuth } from '../auth/AuthContext';
import { authErrorMessage, loginWithGoogle, loginWithPassword, safeNext } from '../auth/login';
import { SiteHeader } from '../components/SiteHeader';

export function LoginPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // En el portal de una marca ({marca}.brands.mx) el login toma su nombre y sus colores.
  const brand = brandFromHost(window.location.host);
  const theme = useBrandTheme(brand);
  const palette = theme ? brandColors(theme.accent, theme.accent2) : null;
  const themeStyle = palette
    ? ({
        '--login-1': palette.primary,
        '--login-on-1': palette.onPrimary,
        '--login-2': palette.secondary,
        '--login-on-2': palette.onSecondary,
      } as CSSProperties)
    : undefined;
  const brandName = theme?.name ?? brand?.toUpperCase();

  if (session) return <Navigate to={next} replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!isValidEmail(email) || !password) {
      setError('Escribe tu correo y tu contraseña.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await loginWithPassword(email, password);
      navigate(next, { replace: true });
    } catch (e) {
      setError(authErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setError(null);
    try {
      await loginWithGoogle();
      navigate(next, { replace: true });
    } catch (e) {
      setError(authErrorMessage(e));
    }
  }

  return (
    <>
      <SiteHeader />
      <main className={`login ${palette ? 'login--brand' : ''}`} style={themeStyle}>
        <section className="login__panel">
          <p className="t-nav login__kicker">
            {brandName ? `${brandName} × Brands&People` : 'Entregas de campaña'}
          </p>
          <h1 className="login__title">Entra a tus entregas</h1>
          <p className="t-lead auth__muted">
            Usa el correo y la contraseña que te compartió Brands&amp;People.
          </p>
          <form className="auth__form" onSubmit={onSubmit} noValidate>
            <label className="field">
              <span className="field__label">Correo electrónico</span>
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                placeholder="nombre@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <div className="field">
              <span className="field__label editor__label">
                <label htmlFor="login-password">Contraseña</label>
                <button
                  type="button"
                  className="btn-link auth__toggle"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </span>
              <input
                id="login-password"
                className="input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error ? (
              <p className="field__error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="btn login__submit" disabled={busy}>
              {busy ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
          <div className="login__help">
            <p className="t-meta auth__muted">
              ¿Olvidaste tu contraseña? Pide una nueva a tu contacto en Brands&amp;People.
            </p>
            <button type="button" className="btn-link auth__team" onClick={() => void onGoogle()}>
              Equipo de la agencia: entrar con Google
            </button>
          </div>
        </section>

        {/* Mosaico de color con el módulo circular de brands.mx */}
        <aside className="login__visual" aria-hidden="true">
          <div className="login__tile login__tile--navy">
            <div className="login__circle">
              <span>Tus campañas</span>
              <span>Cada pieza</span>
              <span>Su porqué</span>
              <span>—</span>
              <span>Descargas</span>
            </div>
          </div>
          <div className="login__tile login__tile--yellow">
            <p className="login__tile-text">Todo lo que entregamos, en un solo lugar.</p>
          </div>
          <div className="login__tile login__tile--ink">
            <p className="t-meta">{brandName ? `${brandName} × Brands&People` : 'Brands&People'}</p>
          </div>
        </aside>
      </main>
    </>
  );
}
